import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Link, Stack, Typography } from "@mui/material";
import { GridLinkOperator } from "@mui/x-data-grid-pro";
import { filterModelToElasticQueryString } from "components/DataGrid/helpers/elasticSerializers";
import { LocationManagementLocation } from "types/locationManagement/location";

interface Props {
  location: LocationManagementLocation;
}

const locationPath = (id: string | number) => `/locations/${id}/edit`;

const locationsInsidePath = (locationId: string | number) => {
  const params = new URLSearchParams();
  params.append("filter[]", `inside,equals,${locationId}`);
  return `/locations?${params.toString()}`;
};

// One id covers this location and every location nested inside it. The inventory index stores that list.
const inventorySearchPath = (locationId: string | number) => {
  const query = filterModelToElasticQueryString({
    items: [
      {
        columnField: "locationAncestorIds",
        operatorValue: "=",
        value: String(locationId),
      },
    ],
    linkOperator: GridLinkOperator.And,
  });
  return `/inventories?${new URLSearchParams({ q: query })}`;
};

export const LocationContents: React.FC<Props> = ({ location }) => {
  const ancestors = location.ancestors || [];

  return (
    <Stack spacing={3} sx={{ mt: 3 }}>
      <section>
        <Typography variant="subtitle1">Inside</Typography>
        {ancestors.length === 0 ? (
          <Typography color="text.secondary">Not inside another location</Typography>
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {ancestors.map((ancestor, index) => (
              <React.Fragment key={ancestor.id}>
                {index > 0 && <Typography color="text.secondary">/</Typography>}
                <Link component={RouterLink} to={locationPath(ancestor.id)}>
                  {ancestor.code}
                </Link>
              </React.Fragment>
            ))}
          </Stack>
        )}
      </section>

      <section>
        <Typography variant="subtitle1">Locations inside</Typography>
        {location.id && (
          <Link component={RouterLink} to={locationsInsidePath(location.id)}>
            View locations inside {location.code}
          </Link>
        )}
      </section>

      <section>
        <Typography variant="subtitle1">Assets inside</Typography>
        {location.id && (
          <Link component={RouterLink} to={inventorySearchPath(location.id)}>
            View assets at {location.code}
          </Link>
        )}
      </section>
    </Stack>
  );
};
